import React, { useEffect, useState } from "react";
import { Save, Plus, Trash2, Truck } from "lucide-react";
import { api } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";

export const AdminShipping = ({ token }) => {
  const [shipping, setShipping] = useState(null);
  const [busy, setBusy] = useState(false);
  const authHeader = { headers: { Authorization: `Bearer ${token}` } };

  useEffect(() => {
    api.get("/admin/config", authHeader).then((res) => setShipping(res.data.shipping)).catch(() => toast.error("Could not load shipping."));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const set = (k, v) => setShipping((s) => ({ ...s, [k]: v }));

  const setTier = (i, k, v) => {
    const tiers = [...shipping.tiers];
    tiers[i] = { ...tiers[i], [k]: v };
    set("tiers", tiers);
  };
  const addTier = () => set("tiers", [...(shipping.tiers || []), { max_units: 12, price: 0 }]);
  const removeTier = (i) => set("tiers", shipping.tiers.filter((_, idx) => idx !== i));

  const save = async () => {
    setBusy(true);
    try {
      const payload = {
        shipping: {
          enabled: shipping.enabled,
          label: shipping.label,
          mode: shipping.mode,
          flat_rate: parseFloat(shipping.flat_rate) || 0,
          tiers: (shipping.tiers || []).map((t) => ({ max_units: parseInt(t.max_units), price: parseFloat(t.price) || 0 })),
        },
      };
      await api.put("/admin/shipping", payload, authHeader);
      toast.success("Shipping settings saved!");
    } catch {
      toast.error("Save failed.");
    } finally {
      setBusy(false);
    }
  };

  if (!shipping) return <div className="text-ashydark">Loading…</div>;

  return (
    <div className="space-y-6">
      <section className="rounded-2xl border border-ashy/30 bg-white p-6">
        <div className="flex items-center gap-3">
          <Truck className="h-6 w-6 text-cocoa" />
          <h3 className="font-heading text-2xl font-bold text-ink">Shipping</h3>
        </div>
        <p className="mt-1 text-sm text-ashydark">Offer shipping alongside pickup. You set the price and print UPS labels yourself.</p>

        <div className="mt-5 flex items-center justify-between rounded-xl bg-babybluelight/50 p-4">
          <div>
            <p className="font-semibold text-ink">Enable shipping</p>
            <p className="text-xs text-ashydark">When off, customers can only choose pickup.</p>
          </div>
          <Switch data-testid="shipping-enabled-switch" checked={shipping.enabled} onCheckedChange={(v) => set("enabled", v)} />
        </div>

        <div className="mt-5">
          <Label>Shipping label (shown to customers)</Label>
          <Input data-testid="shipping-label-input" value={shipping.label} onChange={(e) => set("label", e.target.value)} className="mt-1.5" placeholder="Shipping via UPS" />
        </div>

        <div className="mt-6">
          <Label>Pricing mode</Label>
          <div className="mt-2 flex gap-3">
            <button data-testid="shipping-mode-flat" onClick={() => set("mode", "flat")} className={`flex-1 rounded-xl border-2 p-3 text-sm font-medium transition-colors ${shipping.mode === "flat" ? "border-cocoa bg-babybluelight/60 text-cocoa" : "border-ashy/30 text-ashydark"}`}>
              Flat rate (one price)
            </button>
            <button data-testid="shipping-mode-tiered" onClick={() => set("mode", "tiered")} className={`flex-1 rounded-xl border-2 p-3 text-sm font-medium transition-colors ${shipping.mode === "tiered" ? "border-cocoa bg-babybluelight/60 text-cocoa" : "border-ashy/30 text-ashydark"}`}>
              Tiered (by order size)
            </button>
          </div>
        </div>

        {shipping.mode === "flat" ? (
          <div className="mt-5 max-w-xs">
            <Label>Flat shipping price ($)</Label>
            <Input data-testid="shipping-flat-rate" type="number" step="0.01" value={shipping.flat_rate} onChange={(e) => set("flat_rate", e.target.value)} className="mt-1.5" />
          </div>
        ) : (
          <div className="mt-5">
            <Label>Tiers (charged by total number of cookies in the order)</Label>
            <p className="mb-2 text-xs text-ashydark">A single cookie = 1, a 3-pack = 3, etc. The first tier whose max is ≥ order size applies.</p>
            <div className="space-y-2">
              {(shipping.tiers || []).map((t, i) => (
                <div key={i} className="flex items-center gap-2" data-testid={`shipping-tier-${i}`}>
                  <span className="text-sm text-ashydark">Up to</span>
                  <Input type="number" data-testid={`shipping-tier-max-${i}`} value={t.max_units} onChange={(e) => setTier(i, "max_units", e.target.value)} className="w-24" />
                  <span className="text-sm text-ashydark">cookies →</span>
                  <span className="text-sm text-ashydark">$</span>
                  <Input type="number" step="0.01" data-testid={`shipping-tier-price-${i}`} value={t.price} onChange={(e) => setTier(i, "price", e.target.value)} className="w-28" />
                  <Button size="sm" variant="outline" onClick={() => removeTier(i)} className="rounded-full border-ashy/40 text-destructive"><Trash2 className="h-4 w-4" /></Button>
                </div>
              ))}
            </div>
            <Button size="sm" variant="outline" data-testid="add-tier-button" onClick={addTier} className="mt-3 rounded-full border-ashy/40"><Plus className="mr-1.5 h-4 w-4" /> Add tier</Button>
          </div>
        )}

        <Button data-testid="save-shipping-button" onClick={save} disabled={busy} className="mt-6 rounded-full bg-cocoa text-white hover:bg-cocoadark"><Save className="mr-1.5 h-4 w-4" /> Save shipping settings</Button>
      </section>
    </div>
  );
};
